import React from "react";
import { TableLine } from "../../../../IconV2/react";
import ModalForm from "../../../../Modal/Form";
import Modal from "../../../../Modal";
import t from "../../../../i18n";
import { css } from "@linaria/core";
import { ToolbarButton } from "../../../Toolbar/ToolbarButton";
import useModal from "../../../../lib/hooks/useModal";
import InputV2 from "../../../../Input/InputV2";
import useInput from "../../../../lib/hooks/useInput";
import { spacings } from "../../../../Theme";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { isSelectionInTable } from "../../../utils/isSelectionInTable";

export function TableToolbarButton() {
  const [editor] = useLexicalComposerContext();
  const { modal: isModalOpen, openModal, closeModal } = useModal();
  const [isTableSelection, setIsTableSelection] = React.useState(false);

  const inputRows = useInput("1");
  const errorInputRows = React.useMemo(() => {
    const value = parseInt(inputRows.value);
    if (inputRows.value.trim() === "") {
      return t("markdown.table.modal.form.input.error.rows");
    }
    if (!isNaN(value) && value > 20) {
      return t("markdown.table.modal.form.input.error.max_rows");
    }
    return;
  }, [inputRows]);

  const inputColumns = useInput("1");
  const errorInputColumns = React.useMemo(() => {
    const value = parseInt(inputColumns.value);
    if (inputColumns.value.trim() === "") {
      return t("markdown.table.modal.form.input.error.columns");
    }
    if (!isNaN(value) && value > 20) {
      return t("markdown.table.modal.form.input.error.max_columns");
    }
    return;
  }, [inputColumns]);

  const errors = errorInputRows !== undefined || errorInputColumns !== undefined;

  const handleInputChange = React.useCallback(
    (setValue: (value: React.SetStateAction<string>) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        setValue(isNaN(value) || value < 1 ? "" : String(value));
      },
    [],
  );

  const onTableInsert = React.useCallback(() => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: inputColumns.value,
      rows: inputRows.value,
      includeHeaders: true,
    });
    inputRows.setValue("1");
    inputColumns.setValue("1");
    closeModal();
    return true;
  }, [editor, inputRows, inputColumns, closeModal]);

  React.useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => setIsTableSelection(isSelectionInTable()));
      }),
    [editor],
  );

  return (
    <>
      <ToolbarButton
        onClick={openModal}
        description={
          isTableSelection
            ? t("markdown_editor.table.toolbar.button.disable")
            : t("markdown_editor.table.toolbar.button")
        }
        disabled={isTableSelection}
      >
        <TableLine />
      </ToolbarButton>
      <Modal
        title={t("markdown_editor.table.modal.title")}
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        size="m"
      >
        <ModalForm
          onSubmit={errors ? false : onTableInsert}
          onRequestClose={closeModal}
          className={tableToolbarModalForm}
          okButtonName={t("markdown_editor.table.modal.form.ok_button_name")}
        >
          <InputV2
            className={input}
            type="number"
            label={t("markdown_editor.table.modal.form.input.label.rows")}
            value={inputRows.value}
            onChange={handleInputChange(inputRows.setValue)}
            min={1}
            step={1}
            revertable={false}
            error={errorInputRows}
          />
          <InputV2
            className={input}
            type="number"
            label={t("markdown_editor.table.modal.form.input.label.columns")}
            value={inputColumns.value}
            onChange={handleInputChange(inputColumns.setValue)}
            min={1}
            step={1}
            revertable={false}
            error={errorInputColumns}
          />
        </ModalForm>
      </Modal>
    </>
  );
}

//#region CSS
const tableToolbarModalForm = css`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: ${spacings.m};
`;
const input = css`
  /** Delete revertable button */
  button {
    display: none;
  }
  flex: 1;
`;
//#endregion
